import { CalendarFeed } from './feed';
import {
  AnalystData,
  CalendarDataParams,
  CalendarManagerEvent,
  CalendarParamsMap,
  CalendarType,
  CalendarTypeEvents,
  CompanyEventDetails,
  CompanyEventsQueryParams,
  Dividend,
  RatingsAnalystsParams,
  SEC,
  SecCalendarParams,
} from './entities';
import {
  DividendsCalendarManager,
  DividendsQueryParams,
  normalizeDividendsQueryParams,
} from '@benzinga/calendar-dividends-manager';
import { OptionsActivity, SignalsCalendarManager } from '@benzinga/calendar-option-activity-manager';
import { CommonCalendarQueryParams } from '@benzinga/calendar-commons';
import { Subscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { CalendarStore } from './store';
import { SafePromise, SafeType } from '@benzinga/safe-await';
import { CalendarRequest } from './request';
import { CalendarInputParams, setParams } from './utils';

export class CalendarManager extends Subscribable<CalendarManagerEvent> {
  private session: Session;
  private store: CalendarStore;
  private nextFeedId = 0;
  private request: CalendarRequest;
  private inflightByKey = new Map<string, SafePromise<unknown>>();

  constructor(session: Session) {
    super();
    this.session = session;
    this.store = new CalendarStore();
    this.request = new CalendarRequest(session, String(this.nextFeedId));
  }

  public static getName = () => 'benzinga-calendar';

  public getFeed = (calendarType: CalendarType = 'earnings'): CalendarFeed => {
    const feedId = `${this.nextFeedId++}`;
    return new CalendarFeed(this.session, this.store, calendarType, feedId);
  };

  public getCalendarData = async <T extends CalendarType>(
    calendarType: T,
    params: CalendarParamsMap[T] | CalendarDataParams | CalendarInputParams,
    force?: boolean,
  ): SafePromise<CalendarTypeEvents[T]> => {
    if (!this.request.isUsingProxy()) {
      if (calendarType === 'dividends') {
        return this.fetchDividends(params as DividendsQueryParams, force) as SafePromise<CalendarTypeEvents[T]>;
      }
      if (calendarType === 'optionsActivity') {
        return this.fetchOptionsActivity(params as CommonCalendarQueryParams, force) as SafePromise<
          CalendarTypeEvents[T]
        >;
      }
    }

    const formattedParams: CalendarDataParams = setParams(params as CalendarInputParams);
    const storedCalendarData = this.store.getCalendarData(calendarType, formattedParams);

    if (storedCalendarData && !force) {
      return { ok: storedCalendarData };
    }

    return this.withInflight(`calendar:${calendarType}:${JSON.stringify(formattedParams)}`, force, async () => {
      const response = (await this.request.fetchCalendarData(calendarType, formattedParams)) as SafeType<
        Required<CalendarTypeEvents>[T]
      >;

      const payload = response?.ok;
      if (Array.isArray(payload)) {
        this.store.setCalendarData(calendarType, formattedParams, payload);
      }

      if (response.err) {
        return { err: response.err };
      }

      return { ok: Array.isArray(payload) ? payload : [] };
    });
  };

  public getRatingsAnalysts = async (query: RatingsAnalystsParams): SafePromise<AnalystData | null> => {
    return this.request.getRatingsAnalysts(query);
  };

  public getCompanyEvents = async (query: CompanyEventsQueryParams): SafePromise<CompanyEventDetails[] | null> => {
    return this.request.getCompanyEvents(query);
  };

  public getSecCalendarData = async (query: SecCalendarParams): SafePromise<SEC[] | null> => {
    return this.request.getSecCalendarData(query);
  };

  private fetchDividends = async (params: DividendsQueryParams, force?: boolean): SafePromise<Dividend[]> => {
    const query = normalizeDividendsQueryParams(params);
    const cacheKey = query as unknown as CalendarDataParams;
    const storedCalendarData = this.store.getCalendarData('dividends', cacheKey);

    if (storedCalendarData && !force) {
      return { ok: storedCalendarData };
    }

    return this.withInflight(`dividends:${JSON.stringify(query)}`, force, async () => {
      const response = await this.session.getManager(DividendsCalendarManager).getDividends(query);
      if (response.err) {
        return { err: response.err };
      }

      const payload = response.ok ?? [];
      this.store.setCalendarData('dividends', cacheKey, payload);
      return { ok: payload };
    });
  };

  private fetchOptionsActivity = async (
    params: CommonCalendarQueryParams,
    force?: boolean,
  ): SafePromise<OptionsActivity[]> => {
    const formattedParams = setParams(params as CalendarInputParams);
    const storedCalendarData = this.store.getCalendarData('optionsActivity', formattedParams);

    if (storedCalendarData && !force) {
      return { ok: storedCalendarData };
    }

    return this.withInflight(`optionsActivity:${JSON.stringify(formattedParams)}`, force, async () => {
      const symbols =
        typeof formattedParams.symbols === 'string' ? formattedParams.symbols.split(',').filter(Boolean) : undefined;
      const response = await this.session.getManager(SignalsCalendarManager).getOptionsActivity({
        ...params,
        pageSize: formattedParams.pageSize,
        ...(formattedParams.dateFrom && { dateFrom: formattedParams.dateFrom }),
        ...(formattedParams.dateTo && { dateTo: formattedParams.dateTo }),
        ...(symbols && { symbols }),
      });
      if (response.err) {
        return { err: response.err };
      }

      const payload = response.ok ?? [];
      this.store.setCalendarData('optionsActivity', formattedParams, payload);
      return { ok: payload };
    });
  }

  private withInflight<T>(key: string, force: boolean | undefined, run: () => SafePromise<T>): SafePromise<T> {
    if (!force) {
      const existing = this.inflightByKey.get(key);
      if (existing) {
        return existing as SafePromise<T>;
      }
    }

    const pending = run().finally(() => {
      if (this.inflightByKey.get(key) === pending) {
        this.inflightByKey.delete(key);
      }
    });
    this.inflightByKey.set(key, pending);
    return pending;
  }
}
